import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatDate, timeAgo, formatApiError } from "@/lib/api";
import { PageHeader, StatusPill, PriorityPill, EmptyState } from "@/components/shared/Common";
import { Button } from "@/components/ui/button";
import { Play, Pause, CheckCircle, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function MyQueue() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/cases?status=assigned,in_progress,paused");
      setItems(data.items);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/cases/${id}`, { status });
      toast.success(`Status → ${status.replace("_", " ")}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <PageHeader title="My Queue" subtitle={`${items.length} active case${items.length === 1 ? "" : "s"} assigned to you`} />

      {loading && <div className="text-sm text-muted-foreground">Loading…</div>}
      {!loading && items.length === 0 && (
        <EmptyState title="Queue is clear" subtitle="No assigned or in-progress work at the moment." />
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {items.map(c => (
          <div key={c.id} data-testid={`my-queue-card-${c.case_id}`} className="border rounded-md bg-card p-5 hover:-translate-y-0.5 transition-transform">
            <div className="flex items-start justify-between mb-2">
              <div className="mono text-xs font-medium">{c.case_id}</div>
              <PriorityPill priority={c.priority} />
            </div>
            <div className="text-lg font-semibold tracking-tight leading-tight">{c.customer}</div>
            <div className="text-xs text-muted-foreground mt-1">{c.case_type}</div>
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <StatusPill status={c.status} />
              <span className="text-[11px] mono text-muted-foreground">Due {formatDate(c.due_date)}</span>
            </div>
            <div className="flex items-center gap-2 mt-4">
              <Button data-testid={`open-case-${c.case_id}`} size="sm" variant="outline" onClick={() => nav(`/cases/${c.id}`)}>Open</Button>
              {c.status === "assigned" && <Button size="sm" onClick={() => changeStatus(c.id, "in_progress")}><Play size={12} className="mr-1" /> Start</Button>}
              {c.status === "in_progress" && (
                <>
                  <Button size="sm" variant="outline" onClick={() => changeStatus(c.id, "paused")}><Pause size={12} className="mr-1" /> Pause</Button>
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => changeStatus(c.id, "completed")}><CheckCircle size={12} className="mr-1" /> Done</Button>
                </>
              )}
              {c.status === "paused" && <Button size="sm" onClick={() => changeStatus(c.id, "in_progress")}><Play size={12} className="mr-1" /> Resume</Button>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
